// Small presentation helpers shared by the Customers (CRM) page components.

import React from "react";
import { Chip, Paper, Stack, Tooltip, Typography } from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import type { CrmCustomer, Service, Stage } from "../../shared/crm";
import { ageOn, formatPounds, STAGE_LABEL } from "../../shared/crm";

export type ChipColor = "default" | "primary" | "secondary" | "success" | "info" | "warning" | "error";

export const STAGE_COLOR: Record<Stage, ChipColor> = {
  enquiry: "info",
  registered: "secondary",
  active: "success",
  holidayOnly: "warning",
  lapsed: "error",
  alumni: "default",
};

export const STAGE_HINT: Record<Stage, string> = {
  enquiry: "Asked about a place, nothing booked yet",
  registered: "Registered for preschool, not started yet",
  active: "A child is at preschool now",
  holidayOnly: "Holiday Club in the last 12 months (or booked), no child at preschool",
  lapsed: "No booking for over 12 months, children still young enough to come back",
  alumni: "Left preschool or aged out of Holiday Club",
};

export const money = (pence: number) => formatPounds(pence);

export const initials = (name: string) =>
  name.split(/\s+/).filter((part) => /[A-Za-z]/.test(part)).slice(0, 2).map((part) => part[0]!.toUpperCase()).join("") || "?";

export const shortDate = (iso: string | null) =>
  iso ? new Date(`${iso.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }) : "—";

export const monthDate = (iso: string | null) =>
  iso ? new Date(`${iso.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-GB", { month: "short", year: "numeric", timeZone: "UTC" }) : "—";

/** "3y 4m" */
export const ageText = (dateOfBirth: string | null, today: string) => {
  if (!dateOfBirth) return null;
  const years = ageOn(dateOfBirth, today);
  const months = Math.floor(((Date.parse(today) - Date.parse(dateOfBirth)) / (365.25 / 12 * 864e5)) % 12);
  return years < 0 ? "not born yet" : `${years}y ${Math.max(0, months)}m`;
};

export const ServiceBadge: React.FC<{ service: Service }> = ({ service }) =>
  service === "preschool"
    ? <Chip size="small" variant="outlined" color="primary" icon={<SchoolIcon />} label="Preschool" />
    : <Chip size="small" variant="outlined" color="warning" icon={<BeachAccessIcon />} label="Holiday Club" />;

export const ServiceBadges: React.FC<{ services: Service[] }> = ({ services }) => (
  <>{services.map((s) => <ServiceBadge key={s} service={s} />)}</>
);

export const StageChip: React.FC<{ customer: Pick<CrmCustomer, "stage" | "manualStage" | "stageReason">; size?: "small" | "medium" }> = ({ customer, size = "small" }) => (
  <Tooltip title={`${customer.stageReason}${customer.manualStage ? " (set by hand)" : ""} · ${STAGE_HINT[customer.stage]}`}>
    <Chip size={size} color={STAGE_COLOR[customer.stage]} label={`${STAGE_LABEL[customer.stage]}${customer.manualStage ? " ✎" : ""}`} />
  </Tooltip>
);

export const Stat: React.FC<{ label: string; value: string; hint?: string; children?: React.ReactNode; onClick?: () => void }> =
  ({ label, value, hint, children, onClick }) => (
    <Paper variant="outlined" onClick={onClick}
      sx={{ p: 2, flex: 1, minWidth: 150, cursor: onClick ? "pointer" : "default", "&:hover": onClick ? { borderColor: "primary.main" } : undefined }}>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography variant="h5" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
      {hint && <Typography variant="body2" color="text.secondary">{hint}</Typography>}
      {children}
    </Paper>
  );

export const Section: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode }> = ({ title, action, children }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ flexGrow: 1, textTransform: "uppercase", letterSpacing: 0.5 }}>{title}</Typography>
      {action}
    </Stack>
    {children}
  </Paper>
);
